import { useState, useRef, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError, type GenerationResponse } from "@/lib/api";
import Logo from "@/pages/home/components/Logo";
import { useAuth } from "@/hooks/useAuth";
import VideoPlayer from "@/pages/generate/components/VideoPlayer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/pages/home/components/Footer";
import { currentLanguage, LOCALES } from "@/i18n";
import { plans, planText, type Plan } from "@/lib/plans";
import { hasConsent } from "@/lib/cookieConsent";
import CustomPlanCard from "@/pages/pricing/components/CustomPlanCard";
import PolicyLinks from "@/components/PolicyLinks";

interface GalleryItem {
  id: string;
  prompt: string;
  imageUrl: string;
  created_at: string;
}

interface VideoGalleryItem {
  id: string;
  prompt: string;
  videoUrl: string;
  duration: number;
  provider?: string;
  created_at: string;
}

export default function Generate() {
  const { t } = useTranslation();
  const { user, profile, loading: authLoading, setCredits } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatedExt, setGeneratedExt] = useState("png");
  const [provider, setProvider] = useState<string | null>(null);
  const [isFallback, setIsFallback] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [savingToGallery, setSavingToGallery] = useState(false);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [mode, setMode] = useState<"image" | "video">("image");
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoProvider, setVideoProvider] = useState<string | null>(null);
  const [videoGallery, setVideoGallery] = useState<VideoGalleryItem[]>([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Billing details are collected on the checkout page before DSK is called.
  const handleCheckout = (tier: Plan) => {
    navigate(`/checkout?plan=${encodeURIComponent(tier.name)}`);
  };

  // The galleries are "functional" storage: kept in the browser only with
  // consent, otherwise they last as long as the page.
  useEffect(() => {
    if (!hasConsent("functional")) return;
    const stored = localStorage.getItem("ms-gallery");
    if (stored) {
      try {
        setGallery(JSON.parse(stored));
      } catch {
        /* noop */
      }
    }
  }, []);

  useEffect(() => {
    if (!hasConsent("functional")) return;
    const stored = localStorage.getItem("ms-video-gallery");
    if (stored) {
      try {
        setVideoGallery(JSON.parse(stored));
      } catch {
        /* noop */
      }
    }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, [cooldown]);

  const saveToGallery = (promptText: string, imageUrl: string) => {
    const item: GalleryItem = {
      id: crypto.randomUUID(),
      prompt: promptText,
      imageUrl,
      created_at: new Date().toISOString(),
    };
    const next = [item, ...gallery].slice(0, 20);
    setGallery(next);
    if (hasConsent("functional")) localStorage.setItem("ms-gallery", JSON.stringify(next));
  };

  const saveVideoToGallery = (promptText: string, videoUrl: string, duration: number, provider?: string) => {
    const item: VideoGalleryItem = {
      id: crypto.randomUUID(),
      prompt: promptText,
      videoUrl,
      duration,
      provider,
      created_at: new Date().toISOString(),
    };
    const next = [item, ...videoGallery].slice(0, 20);
    setVideoGallery(next);
    if (hasConsent("functional")) localStorage.setItem("ms-video-gallery", JSON.stringify(next));
  };

  const handleGenerate = async () => {
    if (mode === "video") {
      void handleGenerateVideo();
      return;
    }
    const trimmed = prompt.trim();
    if (!trimmed || generating || cooldown > 0) return;

    if (isLoggedIn && credits <= 0) {
      setGenError(t("generate.errors.outOfCredits"));
      return;
    }

    setGenerating(true);
    setGenError(null);
    setGeneratedImage(null);

    try {
      const data = await api.post<GenerationResponse>("/generate/image", { prompt: trimmed });

      if (data?.imageUrl) {
        setGeneratedImage(data.imageUrl);
        setProvider(data.provider || null);
        setIsFallback(!!data.fallback);
        const mime = (data.mimeType || "image/png").split("/")[1] || "png";
        setGeneratedExt(mime);
        setSavingToGallery(true);
        saveToGallery(trimmed, data.imageUrl);
        setSavingToGallery(false);

        // The server charged the credit and returned the new balance.
        if (data.credits !== null) setCredits(data.credits);
        // Apply a 5-second cooldown between successful requests to avoid rate limits
        setCooldown(5);
      } else {
        throw new Error(t("generate.errors.noImage"));
      }
    } catch (err) {
      setGenError(
        err instanceof ApiError && err.status === 422
          ? t("generate.errors.blocked")
          : err instanceof Error
            ? err.message
            : t("generate.errors.imageFailed")
      );
      // Back off harder when the provider is rate limiting us.
      setCooldown(err instanceof ApiError && err.status === 429 ? 30 : 10);
    } finally {
      setGenerating(false);
      setSavingToGallery(false);
    }
  };

  const handleGenerateVideo = async () => {
    const trimmed = prompt.trim();
    if (!trimmed || videoLoading || cooldown > 0) return;

    if (isLoggedIn && credits < 2) {
      setVideoError(t("generate.errors.videoCost"));
      return;
    }

    setVideoLoading(true);
    setVideoError(null);
    setVideoUrl(null);

    try {
      const data = await api.post<GenerationResponse>("/generate/video", {
        prompt: trimmed,
        duration: videoDuration,
      });

      if (data?.videoUrl) {
        setVideoUrl(data.videoUrl);
        setVideoProvider(data.provider || null);
        saveVideoToGallery(trimmed, data.videoUrl, videoDuration, data.provider);
        if (data.credits !== null) setCredits(data.credits);
        setCooldown(5);
      } else {
        throw new Error(t("generate.errors.noVideo"));
      }
    } catch (err) {
      setVideoError(
        err instanceof ApiError && err.status === 422
          ? t("generate.errors.blocked")
          : err instanceof Error
            ? err.message
            : t("generate.errors.videoFailed")
      );
      setCooldown(err instanceof ApiError && err.status === 429 ? 30 : 10);
    } finally {
      setVideoLoading(false);
    }
  };

  const credits = profile?.credits ?? 0;
  const isLoggedIn = !!user;

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-16 px-4 md:px-6 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3 md:gap-4">
            <LanguageSwitcher />
            <Link
              to="/"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("generate.nav.home")}
            </Link>
            <Link
              to="/profile"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.nav.profile")}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            {t("generate.hero.eyebrow")}
          </span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">
            {t("generate.hero.title")}
          </h1>
          <p className="mt-3 text-foreground-600">
            {t("generate.hero.subtitle")}
          </p>
          <p className="mt-3 text-xs leading-relaxed text-foreground-500">
            <i className="ri-shield-check-line mr-1 text-primary-600"></i>
            <Trans
              i18nKey="common.contentRules"
              components={{ terms: <Link to="/terms-of-use" className="text-primary-600 hover:underline" /> }}
            />
          </p>
        </div>

        {/* Mode Tabs */}
        <div className="mt-8 flex justify-center">
          <div className="inline-flex items-center rounded-full bg-background-200/70 p-1">
            <button
              type="button"
              onClick={() => { setMode("image"); setGenError(null); setVideoError(null); }}
              className={`whitespace-nowrap flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all cursor-pointer ${
                mode === "image"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              <i className="ri-image-line"></i>
              {t("generate.modes.image")}
            </button>
            <button
              type="button"
              onClick={() => { setMode("video"); setGenError(null); setVideoError(null); }}
              className={`whitespace-nowrap flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-all cursor-pointer ${
                mode === "video"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              <i className="ri-video-line"></i>
              {t("generate.modes.video")}
            </button>
          </div>
        </div>

        {/* Prompt area */}
        <div className="mt-6 rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!generating) void handleGenerate();
                }
              }}
              placeholder={mode === "video" ? t("generate.placeholder.video") : t("generate.placeholder.image")}
              rows={4}
              className="w-full rounded-xl border border-background-300/60 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
            />
            <div className="absolute bottom-3 right-3 text-xs text-foreground-400">
              {prompt.length}
            </div>
          </div>

          {mode === "video" && (
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm text-foreground-600 whitespace-nowrap">{t("generate.duration")}</span>
              {[5, 10].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setVideoDuration(d)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-sm font-medium border transition-all cursor-pointer ${
                    videoDuration === d
                      ? "bg-primary-500 text-background-50 border-primary-500"
                      : "border-background-300/60 text-foreground-600 hover:border-primary-400"
                  }`}
                >
                  {t("generate.seconds", { count: d })}
                </button>
              ))}
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {isLoggedIn ? (
                <>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                    credits <= 2 ? "bg-red-100 text-red-900" : credits <= 5 ? "bg-amber-100 text-amber-900" : "bg-accent-100 text-accent-900"
                  }`}>
                    <i className="ri-flashlight-line"></i>
                    {t("generate.creditsLeft", { count: credits })}
                  </span>
                  {credits <= 2 && (
                    <Link
                      to="/pricing"
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-circle-line text-lg"></i>
                      {t("generate.buyMoreCredits")}
                    </Link>
                  )}
                </>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 text-secondary-900 px-3 py-1 text-xs font-medium">
                  <i className="ri-information-line"></i>
                  {t("generate.signInToEarn")}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => void handleGenerate()}
              disabled={(mode === "image" ? generating : videoLoading) || !prompt.trim() || cooldown > 0}
              className="whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-8 py-3 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.03] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2"
            >
              {(mode === "image" ? generating : videoLoading) ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg"></i>
                  {mode === "video" ? t("generate.buttons.generatingVideo") : t("generate.buttons.generating")}
                </>
              ) : cooldown > 0 ? (
                <>
                  <i className="ri-time-line text-lg"></i>
                  {t("generate.buttons.wait", { count: cooldown })}
                </>
              ) : mode === "video" ? (
                <>
                  <i className="ri-video-ai-line text-lg"></i>
                  {t("generate.buttons.generateVideo")}
                </>
              ) : (
                <>
                  <i className="ri-magic-line text-lg"></i>
                  {t("generate.buttons.generate")}
                </>
              )}
            </button>
          </div>

          {genError && mode === "image" && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
              {genError}
            </div>
          )}
          {videoError && mode === "video" && (
            <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
              {videoError}
            </div>
          )}
        </div>

        {/* Generated image */}
        {generatedImage && !generating && mode === "image" && (
          <div className="mt-8 rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-base text-foreground-950">{t("generate.result.title")}</h2>
              <div className="flex items-center gap-3">
                {isFallback && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-xs font-medium">
                    <i className="ri-error-warning-line"></i>
                    {t("generate.result.fallback", { provider: provider || t("generate.result.backupProvider") })}
                  </span>
                )}
                <a
                  href={generatedImage}
                  download={`media-style-generated.${generatedExt}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 text-background-50 px-4 py-2 text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  {t("common.actions.download")}
                </a>
              </div>
            </div>
            <img
              src={generatedImage}
              alt={t("generate.result.imageAlt")}
              className="w-full rounded-xl border border-background-200/70 object-cover"
            />
            <p className="mt-3 text-sm text-foreground-500 line-clamp-2">
              <span className="font-medium text-foreground-700">Prompt:</span> {prompt}
            </p>
          </div>
        )}

        {/* Generated video */}
        {videoUrl && !videoLoading && mode === "video" && (
          <div className="mt-8 rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-base text-foreground-950">{t("generate.result.videoTitle")}</h2>
              <div className="flex items-center gap-3">
                <a
                  href={videoUrl}
                  download={`mediastyle-video-${Date.now()}.jpg`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 text-background-50 px-4 py-2 text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer"
                >
                  <i className="ri-download-line"></i>
                  {t("generate.buttons.downloadImage")}
                </a>
              </div>
            </div>
            <VideoPlayer
              imageUrl={videoUrl}
              duration={videoDuration}
              className="w-full"
            />
            <p className="mt-3 text-sm text-foreground-500 line-clamp-2">
              <span className="font-medium text-foreground-700">Prompt:</span> {prompt}
            </p>
            <p className="mt-1 text-xs text-foreground-400">
              {t("generate.result.videoNote")}
            </p>
          </div>
        )}

        {/* Gallery */}
        {gallery.length > 0 && mode === "image" && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">
              {t("generate.gallery.recentImages")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-background-200/70 bg-background-100 overflow-hidden group"
                >
                  <img
                    src={item.imageUrl}
                    alt={item.prompt}
                    className="w-full aspect-square object-cover"
                  />
                  <div className="p-3">
                    <p className="text-xs text-foreground-500 line-clamp-2">{item.prompt}</p>
                    <p className="mt-1 text-xs text-foreground-400">
                      {new Date(item.created_at).toLocaleDateString(LOCALES[currentLanguage()])}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Video Gallery */}
        {videoGallery.length > 0 && mode === "video" && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">
              {t("generate.gallery.recentVideos")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {videoGallery.map((item) => (
                <div
                  key={item.id}
                  className="rounded-xl border border-background-200/70 bg-background-100 overflow-hidden"
                >
                  <VideoPlayer
                    imageUrl={item.videoUrl}
                    duration={item.duration}
                    className="w-full"
                  />
                  <div className="p-3">
                    <p className="text-xs text-foreground-500 line-clamp-2">{item.prompt}</p>
                    <p className="mt-1 text-xs text-foreground-400">
                      {t("generate.gallery.videoMeta", {
                        count: item.duration,
                        date: new Date(item.created_at).toLocaleDateString(LOCALES[currentLanguage()]),
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Plans */}
      <section className="border-t border-background-200/70 bg-background-100/50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              {t("generate.plans.eyebrow")}
            </span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground-950">
              {t("generate.plans.title")}
            </h2>
            <p className="mt-3 text-foreground-600">
              {t("generate.plans.subtitle")}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {plans.map((tier) => {
              const text = planText(t, tier);
              return (
                <div
                  key={tier.name}
                  className={`relative flex flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 ${
                    tier.highlighted
                      ? "bg-gradient-to-b from-primary-600 to-accent-600 text-background-50 shadow-xl"
                      : "bg-background-50 border border-background-200/70 text-foreground-950"
                  }`}
                >
                  {tier.highlighted && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-background-50 text-primary-600 text-xs font-semibold px-4 py-1 shadow-sm">
                      {t("generate.plans.mostPopular")}
                    </span>
                  )}

                  <h3 className="text-lg font-bold">{tier.name}</h3>
                  <div className="mt-4 flex items-end gap-1">
                    <span className="text-4xl md:text-5xl font-bold tracking-tight">{text.price}</span>
                    <span className={`text-sm mb-2 ${tier.highlighted ? "text-background-50/80" : "text-foreground-500"}`}>
                      {text.period}
                    </span>
                  </div>
                  <p className={`mt-2 text-sm ${tier.highlighted ? "text-background-50/80" : "text-foreground-600"}`}>
                    {text.tagline}
                  </p>
                  <div className={`mt-2 inline-flex items-center gap-1.5 text-sm font-medium ${tier.highlighted ? "text-accent-200" : "text-primary-600"}`}>
                    <i className="ri-coins-line"></i>
                    {text.credits}
                  </div>

                  <ul className="mt-6 flex-1 space-y-3">
                    {text.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <i
                          className={`ri-checkbox-circle-fill text-lg mt-0.5 ${
                            tier.highlighted ? "text-accent-200" : "text-primary-500"
                          }`}
                        ></i>
                        <span className={tier.highlighted ? "text-background-50/90" : "text-foreground-700"}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handleCheckout(tier)}
                    disabled={checkingOut === tier.name}
                    className={`mt-8 w-full whitespace-nowrap rounded-full py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] cursor-pointer ${
                      tier.highlighted
                        ? "bg-background-50 text-primary-700"
                        : "bg-gradient-to-r from-primary-500 to-accent-500 text-background-50"
                    } ${checkingOut ? "opacity-60" : ""}`}
                  >
                    {checkingOut === tier.name ? t("generate.plans.redirecting") : text.cta}
                  </button>
                </div>
              );
            })}
          </div>

          <CustomPlanCard />

          {checkoutError && (
            <div className="mt-8 max-w-md mx-auto rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600 text-center">
              {checkoutError}
            </div>
          )}

          <p className="mt-8 text-center text-xs text-foreground-500 flex items-center justify-center gap-2">
            <i className="ri-lock-line text-primary-600"></i>
            {t("generate.plans.secureCheckout")}
          </p>
          <p className="mt-2 text-center text-xs text-foreground-500">{t("common.prices.note")}</p>
          <PolicyLinks />
        </div>
      </section>

      <Footer />
    </div>
  );
}